import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PageShell } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';

const SHIFTS = {
  M: { label: 'Morning', time: '07:00–15:00', color: '#f59e0b', bg: '#fffbeb' },
  E: { label: 'Evening', time: '15:00–23:00', color: '#8b5cf6', bg: '#f5f3ff' },
  N: { label: 'Night', time: '23:00–07:00', color: '#1d4ed8', bg: '#eff6ff' },
  O: { label: 'Off', time: '—', color: '#94a3b8', bg: '#f8fafc' },
  L: { label: 'Leave', time: '—', color: '#dc2626', bg: '#fef2f2' },
  H: { label: 'Holiday', time: '—', color: '#10b981', bg: '#f0fdf4' },
};

const DEPT_MAP = {
  Nursing: ['nurse'],
  Doctors: ['doctor'],
  Lab: ['lab'],
  Housekeeping: ['housekeeping'],
  HR: ['hr'],
  Pharmacy: ['pharmacy'],
  Billing: ['billing'],
  IPD: ['ipd'],
};

function generateEmptyRoster(staff, daysInMonth) {
  const roster = {};
  staff.forEach((emp) => {
    roster[emp.id] = {};
    for (let d = 1; d <= daysInMonth; d++) roster[emp.id][d] = 'O';
  });
  return roster;
}

function RosterGrid({ staff, daysInMonth, month, shifts, onShiftChange, saving }) {
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const firstDay = new Date(`${month}-01`).getDay();

  function cycleShift(empId, day) {
    const order = ['M', 'E', 'N', 'O', 'L'];
    const current = shifts[empId]?.[day] || 'O';
    const next = order[(order.indexOf(current) + 1) % order.length];
    onShiftChange(empId, day, next);
  }

  if (!staff.length) {
    return <p className="hint">No staff in this department. Add staff logins under Staff &amp; Logins first.</p>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      {saving && <p className="hint" style={{ marginBottom: 8 }}>Saving roster…</p>}
      <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 900 }}>
        <thead>
          <tr>
            <th style={{ padding: '8px 12px', background: '#1e293b', color: '#fff', textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, width: 160 }}>Staff Member</th>
            {days.map((d) => {
              const dayName = weekDays[(firstDay + d - 1) % 7];
              const isWeekend = dayName === 'Su' || dayName === 'Sa';
              return (
                <th key={d} style={{ padding: '4px 6px', background: isWeekend ? '#374151' : '#1e293b', color: isWeekend ? '#fbbf24' : '#fff', textAlign: 'center', minWidth: 36, fontWeight: 700 }}>
                  <div>{d}</div><div style={{ fontSize: 9, opacity: 0.8 }}>{dayName}</div>
                </th>
              );
            })}
            <th style={{ padding: '4px 8px', background: '#0f172a', color: '#94a3b8', textAlign: 'center', minWidth: 60 }}>M/E/N/O</th>
          </tr>
        </thead>
        <tbody>
          {staff.map((emp, rowIdx) => {
            const empRoster = shifts[emp.id] || {};
            const counts = { M: 0, E: 0, N: 0, O: 0, L: 0, H: 0 };
            Object.values(empRoster).forEach((s) => { if (counts[s] !== undefined) counts[s]++; });
            return (
              <tr key={emp.id} style={{ background: rowIdx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                <td style={{ padding: '6px 12px', position: 'sticky', left: 0, background: rowIdx % 2 === 0 ? '#fff' : '#f8fafc', zIndex: 1, borderRight: '2px solid #e2e8f0' }}>
                  <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 12 }}>{emp.name}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{emp.designation}</div>
                </td>
                {days.map((d) => {
                  const shift = empRoster[d] || 'O';
                  const s = SHIFTS[shift];
                  return (
                    <td key={d} style={{ padding: '3px', textAlign: 'center' }}>
                      <button type="button" onClick={() => cycleShift(emp.id, d)} title={`${s.label} (${s.time})`} style={{ width: 30, height: 24, borderRadius: 5, border: 'none', background: s.bg, color: s.color, fontWeight: 900, fontSize: 11, cursor: 'pointer', display: 'block', margin: 'auto' }}>{shift}</button>
                    </td>
                  );
                })}
                <td style={{ padding: '4px 8px', textAlign: 'center', background: '#f8fafc', fontSize: 10, fontWeight: 700 }}>
                  <span style={{ color: '#f59e0b' }}>{counts.M}</span>/<span style={{ color: '#8b5cf6' }}>{counts.E}</span>/<span style={{ color: '#1d4ed8' }}>{counts.N}</span>/<span style={{ color: '#94a3b8' }}>{counts.O}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function DutyRosterPage() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [dept, setDept] = useState('Nursing');
  const [shifts, setShifts] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const daysInMonth = new Date(parseInt(month.split('-')[0], 10), parseInt(month.split('-')[1], 10), 0).getDate();
  const today = now.getDate();

  const { data: staffData } = useLiveData(() => ipApi.getStaff(), []);
  const { data: rosterData, reload: reloadRoster, error: rosterError } = useLiveData(
    () => ipApi.getRoster(month, dept),
    [month, dept],
  );

  const allStaff = staffData?.staff || [];
  const staffByDept = useMemo(() => {
    const map = {};
    Object.keys(DEPT_MAP).forEach((d) => {
      const roles = DEPT_MAP[d];
      map[d] = allStaff
        .filter((s) => roles.includes(s.role) && s.active !== false)
        .map((s) => ({
          id: s.staffId || s.uid || s.email,
          name: s.name || s.email,
          designation: s.designation || s.roleLabel || s.role,
        }));
    });
    return map;
  }, [allStaff]);

  const staff = staffByDept[dept] || [];
  const departments = Object.keys(DEPT_MAP);

  useEffect(() => {
    const saved = rosterData?.roster?.shifts;
    if (saved && Object.keys(saved).length > 0) {
      setShifts(saved);
    } else if (staff.length > 0) {
      setShifts(generateEmptyRoster(staff, daysInMonth));
    } else {
      setShifts({});
    }
  }, [rosterData, staff, daysInMonth, month, dept]);

  const persistRoster = useCallback(async (nextShifts) => {
    setSaving(true);
    setMsg('');
    try {
      await ipApi.saveRoster({ month, dept, shifts: nextShifts });
    } catch (err) {
      setMsg(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [month, dept]);

  const onShiftChange = (empId, day, next) => {
    setShifts((prev) => {
      const nextShifts = { ...prev, [empId]: { ...prev[empId], [day]: next } };
      persistRoster(nextShifts);
      return nextShifts;
    });
  };

  const onDutyToday = useMemo(() => {
    let count = 0;
    let onLeave = 0;
    let night = 0;
    staff.forEach((emp) => {
      const s = shifts[emp.id]?.[today];
      if (s === 'M' || s === 'E' || s === 'N') count++;
      if (s === 'L') onLeave++;
      if (s === 'N') night++;
    });
    return { count, onLeave, night };
  }, [shifts, staff, today]);

  return (
    <PageShell
      title="Duty Roster & Shift Management"
      subtitle="Monthly Roster · Morning / Evening / Night Shifts · Dept-wise Scheduling · Leave Integration"
      actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>🖨 Print Roster</button>}
    >
      {rosterError && (
        <div className="card mb" style={{ borderColor: '#fca5a5', background: '#fef2f2' }}>
          {rosterError} <button type="button" className="btn btn-sm btn-secondary" onClick={reloadRoster}>Retry</button>
        </div>
      )}
      {msg && <p className="hint mb" style={{ color: '#dc2626' }}>{msg}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Dept Staff', val: staff.length, color: '#6366f1' },
          { label: 'On Duty Today', val: onDutyToday.count, color: '#10b981' },
          { label: 'On Leave', val: onDutyToday.onLeave, color: '#dc2626' },
          { label: 'Night Shift', val: onDutyToday.night, color: '#1d4ed8' },
        ].map((s) => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <div className="card" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 12 }}>Month</label><input type="month" className="input" value={month} onChange={(e) => setMonth(e.target.value)} /></div>
          <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 12 }}>Department</label>
            <select className="input" value={dept} onChange={(e) => setDept(e.target.value)}>
              {departments.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
            {Object.entries(SHIFTS).map(([key, val]) => (
              <span key={key} style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: val.bg, color: val.color, border: `1px solid ${val.color}40` }}>{key} = {val.label}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>
          {dept} — Roster for {month}
          <span style={{ fontSize: 13, fontWeight: 500, color: '#94a3b8' }}> ({daysInMonth} days · Click cell to cycle shift)</span>
        </h3>
        <RosterGrid staff={staff} daysInMonth={daysInMonth} month={month} shifts={shifts} onShiftChange={onShiftChange} saving={saving} />
        <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, fontSize: 12, color: '#16a34a', fontWeight: 700 }}>
          💡 Click any shift cell to cycle: Morning → Evening → Night → Off → Leave. Changes save automatically.
        </div>
      </div>
    </PageShell>
  );
}
